// ──────────────────────────────────────────────
// GeoDrops — API Client
// Sends Telegram init data in Authorization header
// for server-side validation.
// ──────────────────────────────────────────────

import type { Pin, CreatePinInput } from "@/types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

function getInitData(): string {
  // Raw init data string from Telegram — NOT initDataUnsafe
  return window.Telegram?.WebApp?.initData || "";
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const initData = getInitData();

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `tma ${initData}`,
      ...options.headers,
    },
  });

  const body = await res.json();

  if (!res.ok) {
    throw new ApiError(body.error || "Request failed", body.code || "UNKNOWN", res.status);
  }

  return body.data;
}

// ── Pin API ──

export async function fetchNearbyPins(
  lat: number,
  lng: number,
  radiusM = 2000,
  category?: string
): Promise<Pin[]> {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    radius_m: String(radiusM),
  });
  if (category) params.set("category", category);
  return request<Pin[]>(`/api/pins/nearby?${params}`);
}

export async function fetchMyPins(): Promise<Pin[]> {
  return request<Pin[]>("/api/pins/mine");
}

export async function createPin(input: CreatePinInput): Promise<Pin> {
  return request<Pin>("/api/pins", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deletePin(id: string): Promise<void> {
  await request(`/api/pins/${id}`, { method: "DELETE" });
}

// ── Error type ──

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}
